import { create } from 'zustand'
import type {
  DateColumn,
  TakenCell,
} from '@/features/reports/lib/order-proposal-math'

/**
 * What the manager is working on in the Bestellvorschlag — the Standort, the order days and the
 * typed Bestellung — kept in memory for the browser session (CHG-2026-10-edi-purchase-orders, D12):
 * "Bestellung senden" opens the new draft, and coming back finds the report as it was left.
 *
 * `taken` marks the rows whose Bestellung was taken from one Vorschlag cell, so that cell stays
 * highlighted until the number is changed by hand or by the fill bar.
 *
 * Deliberately not persisted: a reload still opens on the next weekday with an empty Bestellung
 * (CHG-2026-10-bestellvorschlag, D6, D9).
 */
interface OrderProposalSessionState {
  restaurantId: number | null
  delivery: DateColumn | null
  orders: Record<string, string>
  taken: Record<string, TakenCell>
  setRestaurantId: (id: number) => void
  setDelivery: (delivery: DateColumn) => void
  setOrders: (
    next: (current: Record<string, string>) => Record<string, string>,
  ) => void
  setTaken: (
    next: (current: Record<string, TakenCell>) => Record<string, TakenCell>,
  ) => void
  reset: () => void
}

export const useOrderProposalSessionStore = create<OrderProposalSessionState>()(
  (set) => ({
    restaurantId: null,
    delivery: null,
    orders: {},
    taken: {},
    setRestaurantId: (restaurantId) => set({ restaurantId }),
    setDelivery: (delivery) => set({ delivery }),
    setOrders: (next) => set((state) => ({ orders: next(state.orders) })),
    setTaken: (next) => set((state) => ({ taken: next(state.taken) })),
    reset: () =>
      set({ restaurantId: null, delivery: null, orders: {}, taken: {} }),
  }),
)
